import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Form, message } from 'antd';
import PageTitle from 'apps/home/components/PageTitle';
import ErrorMessage from 'common/ui/ErrorMessage';
import LoadingSpinner from 'common/ui/LoadingSpinner';
import { useNavigate, useParams } from 'react-router-dom';
import { convertFormData, normalizeData } from 'utils/formData.js';

const AdminEdit = ({
  ItemForm,
  listUrl,
  apiCall,
  resourceName,
  options = {},
  title,
}) => {
  const [form] = Form.useForm();
  const params = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    data: response,
    isLoading,
    error: fetchItemError,
  } = useQuery({
    queryKey: [apiCall.detail.queryKey, params.id || params],
    queryFn: () => apiCall.detail.queryFn(params),
  });

  const item = response?.[resourceName || 'item'] || response;

  const {
    mutate: submitItem,
    isPending: isSubmitting,
    error: submitItemError,
  } = useMutation({
    mutationFn: apiCall.edit.queryFn,
    onSuccess: () => {
      // Invalidate the detail query to refetch fresh data
      queryClient.invalidateQueries({
        queryKey: [apiCall.detail.queryKey, params.id || params],
      });
      // Also invalidate the list query if it exists
      if (apiCall.list?.queryKey) {
        queryClient.invalidateQueries({
          queryKey: [apiCall.list.queryKey],
        });
      }
      message.success('Updated successfully');
      navigate(listUrl);
    },
  });

  const {
    mutate: deleteItem,
    isPending: isDeleting,
    error: deleteItemError,
  } = useMutation({
    mutationFn: apiCall.delete?.queryFn,
    onSuccess: () => {
      // Invalidate queries after deletion
      queryClient.invalidateQueries({
        queryKey: [apiCall.detail.queryKey],
      });
      if (apiCall.list?.queryKey) {
        queryClient.invalidateQueries({
          queryKey: [apiCall.list.queryKey],
        });
      }
      message.success('Deleted successfully');
      navigate(listUrl);
    },
  });

  const onFinish = (values, photoFields = ['photo']) => {
    const postData = async () => {
      try {
        let submitData = normalizeData(values);
        let isConvertFormData = false;

        for (let i = 0; i < photoFields.length; i++) {
          const field = photoFields[i];
          const fieldValue = values[field];
          if (
            fieldValue &&
            fieldValue.at(-1) &&
            fieldValue.at(-1).originFileObj instanceof File
          ) {
            submitData[field] = values[field][0].originFileObj;
            isConvertFormData = true;
          } else {
            delete submitData[field];
          }
        }

        if (isConvertFormData) {
          submitData = convertFormData(submitData);
        }
        submitItem({ ...params, payload: submitData });
      } catch (error) {
        console.error(error.message);
      }
    };

    postData();
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (fetchItemError) {
    return <ErrorMessage {...fetchItemError} />;
  }

  return (
    <>
      <PageTitle title={`${title || 'Edit Item'} `} backUrl={listUrl} />
      <ItemForm
        params={params}
        submiting={isSubmitting}
        submitItemError={submitItemError}
        deleting={isDeleting}
        deleteItemError={deleteItemError}
        form={form}
        onFinish={onFinish}
        initialValues={item}
        name="edit_form"
        visibility="visible"
        onDelete={() => {
          if (confirm('Are you sure?')) {
            deleteItem(params);
          }
        }}
      />
    </>
  );
};

export default AdminEdit;
